<template>
    <div v-if="entry">
        <DataTable
            :data="adjustments"
            :columns="[
                {
                    title: 'Scenario',
                    field: 'scenario_name',
                },
                {
                    title: 'Original',
                    field: 'original_cost',
                    class: 'tw-text-right',
                    headerClass: 'tw-text-right',
                },
                {
                    title: 'Adjustment',
                    field: 'value',
                    class: 'tw-text-right',
                    headerClass: 'tw-text-right',
                },
                {
                    title: 'Budgeted',
                    field: 'budgeted_cost',
                    class: 'tw-text-right',
                    headerClass: 'tw-text-right',
                },
                {
                    title: '',
                    field: 'actions',
                    class: 'tw-text-right',
                },
            ]"
        >
            <template #column(scenario_name)="{ row }">
                {{ row.scenario.name }}
            </template>

            <template #column(original_cost)>
                {{ localise(parseFloat(entry.budgeted_cost), 2, true) }}
            </template>

            <template #column(value)="{ row }">
                <span v-if="row.adjustment_type === 'percentage'">
                    {{ localise(parseFloat(row.value), 2, false) }}%
                </span>
                <span v-else>
                    {{ localise(parseFloat(row.value), 2, true) }}
                </span>
            </template>

            <template #column(budgeted_cost)="{ row }">
                <span v-html="adjustmentIndicator(row.value)"></span>
                <span v-if="row.adjustment_type === 'percentage'">
                    {{ localise(parseFloat(entry.budgeted_cost) * (1 + row.value / 100), 2, true) }}
                </span>
                <span v-else>
                    {{ localise(parseFloat(entry.budgeted_cost) + parseFloat(row.value), 2, true) }}
                </span>
            </template>

            <template #column(actions)="{ row }">
                <Button @click="openAdjustment(row)">
                    <i class="mdi mdi-pencil"></i>
                </Button>
            </template>

            <template #empty>
                <p class="empty-label tw-px-2 tw-w-full tw-mb-0">
                    There are currently no scenario adjustments for this budget entry.
                </p>
            </template>
        </DataTable>

        <div class="tw-px-3">
            <Button
                v-if="Object.keys(availableScenarios).length && !eventIsClosed"
                type="is-primary"
                class="tw-flex tw-justify-center tw-gap-2 tw-w-full"
                @click="openAdjustment()"
            >
                <i class="mdi mdi-plus"></i>
                New Scenario Adjustment
            </Button>
            <Button
                v-else-if="!eventIsClosed"
                class="tw-flex tw-justify-center tw-gap-2 tw-w-full"
                type="is-success"
                @click="createScenario"
            >
                <i class="mdi mdi-plus"></i>
                Create A New Scenario
            </Button>
        </div>

        <ModalScenarioAdjustment ref="scenarioAdjustmentModal" :entry="entry" :adjustments="adjustments" />
    </div>
</template>

<script>
import ModalScenarioAdjustment from '@/js/components/modals/Scenarios/Adjustment.vue';
import Button from '@/js/components/controls/Button.vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import DataTable from '@/js/components/tables/DataTable.vue';
import { localise, toast } from '@/js/utils.js';

export default {
    components: { ModalScenarioAdjustment, Button, DataTable },
    props: {
        entry: {
            type: Object,
            default() {
                return null;
            },
        },
    },
    data() {
        const store = useDataStore();
        return {
            adjustments: [],
            store: store,
            scenarios: null,
        };
    },
    computed: {
        eventIsClosed() {
            return this.store.get('current_event.is_closed');
        },
        availableScenarios() {
            if (!this.scenarios) {
                return [];
            }
            const withAdjustments =
                this.adjustments.map((adj) => {
                    return adj.scenario_id;
                }) ?? [];

            return this.scenarios.filter((scenario) => !withAdjustments.includes(scenario.id));
        },
    },
    mounted() {
        Eventbus.$on('reload:adjustmentsTab', () => {
            this.fetchAdjustments();
        });
        this.fetchAdjustments();
    },
    methods: {
        localise,
        createScenario() {
            Eventbus.$emit('close:BudgetEntry');
            Eventbus.$emit('scenario:drawerOpen');
        },
        openAdjustment(adjustment = null) {
            this.$refs.scenarioAdjustmentModal.open(adjustment);
        },
        async fetchAdjustments() {
            if (!this.entry) {
                return;
            }
            try {
                const { data: scenarios } = await axios.get(
                    route('api.account.event.scenarios', {
                        event: this.entry.event_id,
                    })
                );
                this.scenarios = scenarios;
                this.adjustments = [];

                const { data: adjustments } = await axios.get(
                    route('api.account.event.entries.adjustments', {
                        event: this.entry.event_id,
                        entry: this.entry.id,
                    })
                );
                this.adjustments = adjustments;
            } catch (error) {
                toast('Error', 'Error fetching scenario adjustments: ' + error.message, 'danger');
            }
        },
        adjustmentIndicator(value) {
            if (!value) {
                return '';
            }

            return parseFloat(value) > 0
                ? '<i class="mdi mdi-arrow-up-bold"></i>'
                : '<i class="mdi mdi-arrow-down-bold"></i>';
        },
    },
};
</script>
